"use client";

import { faArrowRightToBracket } from "@fortawesome/pro-solid-svg-icons/faArrowRightToBracket";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PasswordInput, Text, TextInput } from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { type FormEvent, useCallback, useState } from "react";

import { userService } from "@/app/api/user";
import { AddPhoneForm } from "@/components/add-phone-form";
import { AuthFormContainer } from "@/components/auth-form-container";
import { VerifyPhoneForm } from "@/components/verify-phone-form";
import { LoginState, useUserContext } from "@/context/user.context";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { MFAStatus } from "@/types/user";
import { MANTINE_COLORS } from "@/utils/ui";
import { buildTestId } from "@/utils/helpers";

export default function Login() {
  const track = useTrackAnalytics();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useInputState("");
  const [phone, setPhone] = useState("");
  const [mfaToken, setMFAToken] = useState("");
  const [loginState, setLoginState] = useState(LoginState.Initial);
  const [isFirstTimeMFASetup, setIsFirstTimeMFASetup] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState<string>();
  const [error, setError] = useState("");
  const router = useRouter();
  const { login } = useUserContext();

  const loginCatchCallback = useCallback(
    (unauthorizedMessage: string) =>
      ({
        response: {
          data: { message },
          status,
        },
      }: {
        response: { data: { message: string }; status: number };
      }) => {
        switch (status) {
          case 401:
            return setError(unauthorizedMessage);
          case 429:
            return setError(message);
          default:
            return setError(
              "There was an issue communicating with the server. Please try again.",
            );
        }
      },
    [],
  );

  const onLoginSuccess = useCallback(
    (email: string) => {
      track("Login Clicked", { email });
      router.push("/");
    },
    [router, track],
  );

  const submitEmailPassword = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setError("");

      login(email, password)
        .then(({ maskedDestination, status }) => {
          switch (status) {
            case MFAStatus.Success:
              return onLoginSuccess(email);
            case MFAStatus.MFARequired: {
              if (maskedDestination) {
                setMaskedPhone(maskedDestination);
              }
              return setLoginState(LoginState.MFARequired);
            }
            case MFAStatus.MFASetupRequired:
              setIsFirstTimeMFASetup(true);
              return setLoginState(LoginState.MFASetupRequired);
          }
        })
        .catch(loginCatchCallback("The email or password is incorrect"));
    },
    [email, login, loginCatchCallback, onLoginSuccess, password],
  );

  const submitPhoneNumber = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setError("");

      userService
        .sendSMSCode(email, phone)
        .then(() => setLoginState(LoginState.MFARequired))
        .catch(() =>
          setError(
            "There was an issue communicating with the server. Please try again.",
          ),
        );
    },
    [email, phone],
  );

  const submitMFA = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setError("");

      // Only pass through phone number if it has been given a value beyond "",
      // otherwise BE will try to parse the empty string as a phone number.
      login(email, password, mfaToken, phone || undefined)
        .then(() => {
          onLoginSuccess(email);
        })
        .catch(loginCatchCallback("The provided code is incorrect"));
    },
    [
      email,
      login,
      loginCatchCallback,
      mfaToken,
      onLoginSuccess,
      password,
      phone,
    ],
  );

  if (loginState === LoginState.MFASetupRequired) {
    return (
      <AddPhoneForm
        errorMessage={error}
        onChange={setPhone}
        onSubmit={submitPhoneNumber}
        phoneNumber={phone}
      />
    );
  }

  if (loginState === LoginState.MFARequired) {
    return (
      <VerifyPhoneForm
        errorMessage={error}
        isFirstTimeMFASetup={isFirstTimeMFASetup}
        onChange={setMFAToken}
        onSubmit={submitMFA}
        phoneNumberLastTwo={(maskedPhone || phone).slice(-2)}
      />
    );
  }

  return (
    <AuthFormContainer
      bottomContent={
        <Link href="/auth/forgot">
          <Text
            c={MANTINE_COLORS.primary}
            className="my-2 cursor-pointer"
            fw={600}
            size="xs"
          >
            Forgot Password?
          </Text>
        </Link>
      }
      errorMessage={error}
      onSubmit={submitEmailPassword}
      submitButtonContent="Log In"
      submitButtonProps={{
        rightSection: <FontAwesomeIcon icon={faArrowRightToBracket} />,
      }}
      title="Log into Upwage"
    >
      <TextInput
        className="my-2 w-full"
        data-testid={buildTestId("login", "form", "email-input")}
        onChange={(e) => setEmail(e.target.value.toLowerCase())}
        placeholder="Email Address"
      />
      <PasswordInput
        className="my-2 w-full"
        data-testid={buildTestId("login", "form", "password-input")}
        onChange={setPassword}
        placeholder="Password"
      />
    </AuthFormContainer>
  );
}
